import Eyebrow from '../Eyebrow';
import ConvergenceField from '../ConvergenceField';
import HeroCtas from './HeroCtas';

/**
 * Homepage hero.
 *
 * Copy is the content brief's, verbatim. Layout, type scale and colour usage
 * are the design reference's. That split is BRAND-SPEC.md's own precedence
 * rule: where the brief conflicts with the designs, the brief wins on copy and
 * the designs win on everything else.
 *
 * Fixed by #7919 and not relitigated here: the section id, the --sand-3 field,
 * and the 640px desktop height.
 *
 * LAYOUT. The reference is a single fixed 640px desktop canvas with zero media
 * queries, so every responsive value below is derived rather than lifted:
 *
 *                    <768              768-1023          >=1024
 *   height           content-driven    560px             640px
 *   copy             normal flow       absolute, centred absolute, centred
 *   gutters          24px              40px              72px
 *
 * Mobile bottom padding was 300px, reserving the band the convergence point
 * occupied below the copy. #7943 removed the graphic below 768px, so that
 * reservation is now 300px of empty sand between the CTAs and the logo strip,
 * pushing the hero past a phone screen for nothing. It drops to 64px, which is
 * the rhythm Section's own mobile spacing uses either side of it. Height stays
 * content-driven rather than fixed, because a fixed value clips a wrapped
 * headline on a 320px screen.
 *
 * isolation: isolate is load-bearing, not decoration. The convergence plate
 * composites with mix-blend-mode: multiply, and without an explicit stacking
 * context on this section it would blend against whatever the page root had
 * already painted. That happens to give the right result today and is one
 * wrapper element away from not. Isolating confines the blend to the hero's
 * own --sand-3 field.
 */
export default function HeroSection() {
  return (
    <section
      id="hero"
      className="relative isolate overflow-hidden bg-sand-3 md:h-[560px] lg:h-[640px]"
    >
      <ConvergenceField />

      <div className="relative px-6 pt-[52px] pb-16 md:absolute md:inset-0 md:flex md:items-center md:p-0">
        <div className="mx-auto w-full max-w-[1360px] md:px-10 lg:px-[72px]">
          <div className="md:max-w-[440px] lg:max-w-[560px] xl:max-w-[640px]">
            <Eyebrow className="pb-[22px] text-[11px] min-[480px]:text-xs">
              Strategy and delivery from one person
            </Eyebrow>

            <h1 className="m-0 text-balance font-display text-[34px] font-medium leading-[1.06] tracking-[-0.035em] text-ink min-[480px]:text-[40px] min-[480px]:leading-[1.05] md:text-[46px] md:leading-[1.04] lg:text-[54px] lg:leading-[1.035] xl:text-[64px] xl:leading-[1.03]">
              Most AI consultants hand you a roadmap.{' '}
              <span className="text-ink-blue">I build what&apos;s on it.</span>
            </h1>

            <p className="mt-[26px] max-w-[520px] text-pretty font-sans text-[17px] leading-[1.62] text-body md:text-[18px] md:leading-[1.64] lg:text-[19px] lg:leading-[1.65]">
              25 years of product leadership, two acquisitions, and platforms powering Apple,
              Red Bull, Disney, BBC and ESPN. I define the strategy and the roadmap, then build
              the systems that deliver them. Same person, start to finish.
            </p>

            <HeroCtas />
          </div>
        </div>
      </div>
    </section>
  );
}
